import { classnames, isLockedVault, isRestrictedVault } from 'helpers/utils';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { goToPage, RouteName } from 'routes';
import { Checkbox } from 'common/checkbox';
import { VaultAvatar } from 'common/vault-avatar';
import { useContext, useEffect, useState } from 'react';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { Spinner } from 'common/spinner';
import { ArrowUpRight } from 'assets/icons';
import { CheckboxType } from 'common/checkbox/checkbox';
import { AppContext } from 'context';
import styles from './menu.module.scss';

const CardMenuItem: React.FC<{
  vault: VaultType;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ vault, isSelected, onSelect }) => (
  <button
    type="button"
    className={classnames(styles.vaultItemContainer, {
      [styles.current]: isSelected,
    })}
    onClick={onSelect}
  >
    <div className={styles.vaultItem}>
      <VaultAvatar
        imageURL={vault.coverThumbnailUrl}
        isLockedOrRestrictedVault={
          isLockedVault(vault) || isRestrictedVault(vault)
        }
        isSmall
      />
      <span className="text__body__regular__small__gray800">{vault.title}</span>
    </div>
    <Checkbox
      isChecked={isSelected}
      itemKey={vault.id.toString()}
      type={CheckboxType.Radio}
    />
  </button>
);

interface MenuProps {
  currentVaultId: number;
  onVaultSelect: (vaultId: number) => void;
}

export const Menu: React.FC<MenuProps> = ({
  currentVaultId,
  onVaultSelect,
}) => {
  const [vaults, setVaults] = useState<VaultType[] | undefined>(undefined);
  const { state } = useContext(AppContext);

  const fetchVaults = async () => {
    const { data } = await VaultsController.getVaults({
      pageNum: 1,
      pageSize: 10,
      sortBy: 'title:asc',
    });
    const vaultsSorted = data.slice().sort((a, b) => {
      if (a.id === currentVaultId) {
        return -1;
      }
      if (b.id === currentVaultId) {
        return 1;
      }
      return 0;
    });

    setVaults(vaultsSorted);
  };

  useEffect(() => {
    if (state.logged) fetchVaults();
  }, []);

  const goToVault = (vaultId: number) => () => {
    goToPage(RouteName.VaultDetail, { id: vaultId });
    onVaultSelect(vaultId);
  };

  return (
    <div className={styles.menuContainer}>
      <div className={styles.header}>
        <Button
          buttonStyle={ButtonStyle.SecondaryGray}
          buttonSize={ButtonSize.Small}
          onClick={() => goToPage(RouteName.Vaults)}
          className={styles.allVaultsButton}
        >
          All Vaults
          <ArrowUpRight />
        </Button>
      </div>
      {vaults ? (
        <div className={styles.vaultList}>
          {vaults.map((vault) => (
            <CardMenuItem
              key={vault.id}
              vault={vault}
              isSelected={vault.id === currentVaultId}
              onSelect={goToVault(vault.id)}
            />
          ))}
        </div>
      ) : (
        <div className={classnames(styles.spinnerContainer)}>
          <Spinner />
        </div>
      )}
    </div>
  );
};
